/*
 * Abstraction for management of table app row
 * selection state. Wraps browser localStorage to
 * cache selected rows across page loads.
 *
 * Provides a store, with an interface for
 * updates and state interrogation.
 */
class TableAppCache {
  // Prefix for all cached keys
  _PREFIX = 'TAC';

  // Ids for special (non-row) keys
  _ALL = 'ALL'
  _ID = 'ID'

  /*
   * @param appId: identifies which table app
   *   is being cached.
   * @param orgId: identifies which org the
   *   cached rows belong to.
   * @param rowCount: total number of rows across
   *   all pages of data.
   *
   * A cache is identified by the dataset it refers
   * to, which is determined by the org, app, and
   * search/filter selections.
   */
  constructor({
    appId,
    orgId,
    rowCount,
  }) {
    this._rowCount = parseInt(rowCount);
    const filterId = this._getFilterId(window.location.search);
    this._id = `${appId}_${orgId}_${filterId}`;
    this._clearIfStale();
    this._addCacheId();
  }

  /*
   * Determine if cache is stale, and clear if so.
   */
  _clearIfStale() {
    const existingCacheId = this._getCacheId();
    if (existingCacheId != this._id) {
      this.clear();
    }
  }

  /*
   * All cached keys share a prefix to distinguish
   * the cached items from other items in localStorage.
   */
  _getPrefixedKey(key) {
    return `${this._PREFIX}-${key}`;
  }

  /*
   * Returns a special key that holds the cache id.
   */
  _getCacheIdKey() {
    return this._getPrefixedKey(this._ID);
  }

  /*
   *
   * Returns a special key that holds the "all mode" state.
   */
  _getAllRowsKey() {
    return this._getPrefixedKey(this._ALL);
  }

  /*
   * Sets the cache id in localStorage to identify
   * the dataset being cached
   */
  _addCacheId() {
    localStorage[this._getCacheIdKey()] = this._id;
  }

  _removeCacheId() {
    delete localStorage[this._getCacheIdKey()];
  }

  _getCacheId() {
    return localStorage[this._getCacheIdKey()];
  }

  _add(key) {
    localStorage[this._getPrefixedKey(key)] = 1;
  }

  _remove(key) {
    delete localStorage[this._getPrefixedKey(key)];
  }

  _get(key) {
    return localStorage[this._getPrefixedKey(key)];
  }

  /*
   * Clear all table app cache entries for table selections,
   * keeping the table app cache id.
   */
  clearSelections() {
    const cacheIdKey = this._getCacheIdKey();
    for (const key of Object.keys(localStorage)) {
      if (key === cacheIdKey) {
        continue;
      }
      if (key.startsWith(this._PREFIX)) {
        delete localStorage[key];
      }
    }
  }

  /*
   * Clear all table app cache entries, including id and any other keys
   * added by the instance.
   */
  clear() {
    this.clearSelections();
    this._removeCacheId();
  }

  _setAllMode(on) {
    if (on) {
      this._add(this._ALL);
    } else {
      this._remove(this._ALL);
    }
  }

  /*
   * Determine if all rows are clicked.
   * Note: "clicked" is different than "selected"
   */
  _allRowsClicked() {
    return this.getClickedRowIds().length === this._rowCount;
  }

  /*
   * Determine if no rows are clicked
   * Note: "clicked" is different than "selected"
   */
  _noRowsClicked() {
    return this.getClickedRowIds().length === 0;
  }

  /*
   * Determine if all rows are selected
   * Note: "selected" is different than "clicked"
   */
  allRowsSelected() {
    if (this.isAllMode()) {
      return this._noRowsClicked();
    } else {
      return this._allRowsClicked();
    }
  }

  /*
   * Determine if no rows are selected
   * Note: "selected" is different than "clicked"
   */
  noRowsSelected() {
    if (this.isAllMode()) {
      return this._allRowsClicked();
    } else {
      return this._noRowsClicked();
    }
  }

  /*
   * Interface that updates cache based on user click
   * event on an individual row's checkbox. Toggles
   * state of row in cache.
   */
  onClickRow(rowId) {
    if (this._get(rowId)) {
      this._remove(rowId);
    } else {
      this._add(rowId);
    }
  }

  /*
   * Interface that updates cache based on user click
   * event on the "all" checkbox. If no rows are selected,
   * selects all rows; otherwise, deselects all rows.
   */
  onClickAll() {
    if (this.noRowsSelected()) {
      this.clearSelections();
      this._setAllMode(true);
    } else {
      this.clearSelections();
    }
  }

  /*
   * Interface that returns an array of row ids for
   * clicked rows.
   *
   * Ids may represent either deselected or selected rows,
   * depending on if in all mode or not, respectively.
   */
  getClickedRowIds() {
    const result = [];
    for (const key of Object.keys(localStorage)) {
      if (
        key.startsWith(this._PREFIX)
        && !key.endsWith(this._ALL)
        && !key.endsWith(this._ID)
      ) {
        const rowId = key.split('-')[1];
        result.push(rowId);
      }
    }
    return result;
  }

  /*
   * Returns whether the cache is in "all mode" or not.
   *
   * If in all mode, stored row ids represent deselected rows
   * to be excluded rather than selected rows to be included.
   *
   * All mode is turned on and off when a user clicks
   * the "all" checkbox to select and deselect all rows,
   * respectively.
   */
  isAllMode() {
    return Boolean(this._get(this._ALL));
  }

  /*
   * Interface to get number of rows selected
   * across all pages
   */
  getNumSelected() {
    const numClicked = this.getClickedRowIds().length;
    if (!this.isAllMode()) {
      return numClicked;
    } else {
      return this._rowCount - numClicked;
    }
  }

  /*
   * Given a query string of search params, returns
   * a new string that can be used to uniquely identify
   * the set of filters.
   *
   * ex 1)
   * Excludes `page` key, which is irrelevant to whole dataset.
   * Both of the following return ?q=foo, making them the same filter:
   * ?q=foo&page=1
   * ?q=foo&page=2
   *
   *
   * ex 2) param sorting
   * Sorts params. Both of the following return ?q=foo&alpha=1&type=1,
   * making them the same filter:
   * ?q=foo&alpha=1&beta=1
   * ?q=foo&beta=1&alpha=1
   *
   */
  _getFilterId(search) {
    const searchParams = new URLSearchParams(search);
    searchParams.sort();
    const ignoreKeys = new Set(['page']);
    for (const key of searchParams.keys()) {
      if (ignoreKeys.has(key)) {
        searchParams.delete(key);
      }
    }
    return searchParams.toString();
  }

}

export default TableAppCache;
